import { View } from "@-/com.npmjs.react-native";
import Animated, {
  runOnJS,
  type SharedValue,
  useAnimatedReaction,
  useAnimatedStyle,
  withTiming,
} from "@-/com.npmjs.react-native-reanimated";
import Svg, {
  Circle,
  Defs,
  Ellipse,
  G,
  Line,
  Path,
  RadialGradient,
  Stop,
} from "@-/com.npmjs.react-native-svg";
import {
  WELCOME_GALAXY_GLYPH_BOX,
  WELCOME_GALAXY_GLYPHS,
} from "@-/dev.improve.haniverse.data.welcome-galaxy-glyphs";
import { useMemo, useState } from "react";
import type { WelcomeGalaxyLaunchKind } from "#welcome-galaxy-launch";
import { WelcomeGalaxyLaunchSky } from "#welcome-galaxy-launch-sky";
import {
  welcomeGalaxyPalette,
  type WelcomeGalaxyAppearance,
  type WelcomeGalaxyGradient,
} from "#welcome-galaxy-palette";
import {
  computeWelcomeGalaxyFrameState,
  createWelcomeGalaxyParticles,
  writeWelcomeGalaxySprites,
  writeWelcomeGalaxyShootOutStreaks,
} from "#welcome-galaxy-particles";
import {
  computeWelcomeGalaxyCamera,
  WELCOME_GALAXY_SETTLED_T,
  welcomeGalaxyMaterialize,
  type WelcomeGalaxyViewport,
} from "#welcome-galaxy-timeline";

/** How long the stand-in takes to crossfade to the Skia scene once the scene has joined. */
const STAND_IN_CROSSFADE_MS = 400;

/** Small enough for react-native-svg to lay out instantly; the stream is gone once settled. */
const FALLBACK_BUDGET = { fieldStars: 60, glyphs: 36, stars: 150, streamGlyphs: 0 };
/** Unit metrics: the frame's RSXform scale then comes out directly in dp. */
const FALLBACK_METRICS = { cellSize: 1, glyphSize: 1, starSize: 1 };
const FALLBACK_SEED = 0x4a_4e_56_45;
const GLYPH_SPRITE_COUNT = WELCOME_GALAXY_GLYPHS.length;
const STAR_SPRITE_INDEX = GLYPH_SPRITE_COUNT;
const TONE_MARK_SPRITE_INDEX = STAR_SPRITE_INDEX + 1;

/**
 * A palette gradient's `Stop`s, as elements rather than a component: react-native-svg reads them
 * off the gradient's direct children. Without the scene's blending, the fallback's halo and core
 * are only a rough copy of it.
 */
function gradientStops({ stops }: WelcomeGalaxyGradient) {
  return stops.map(({ alpha, offset, rgb: [red, green, blue] }) => (
    <Stop
      key={offset}
      offset={offset}
      stopColor={`rgb(${red}, ${green}, ${blue})`}
      stopOpacity={alpha}
    />
  ));
}

/** A 0xAARRGGBB sprite colour as CSS. */
function toRgba(color: number): string {
  return `rgba(${(color >>> 16) & 255}, ${(color >>> 8) & 255}, ${color & 255}, ${(color >>> 24) / 255})`;
}

/**
 * A still, settled galaxy drawn with react-native-svg, for builds that opt out of Skia (the Maestro
 * APK, whose GPU-less emulator Skia's Graphite backend would crash), and for the stand-in
 * (`WelcomeGalaxyStandIn`) until the Skia scene is ready. It is the same seeded galaxy at
 * `WELCOME_GALAXY_SETTLED_T`, in the same `appearance`, just with far fewer particles.
 */
export function WelcomeGalaxyStaticFallback({
  appearance,
  testID = "auth-welcome-galaxy-static-fallback",
  viewport,
}: {
  appearance: WelcomeGalaxyAppearance;
  testID?: string;
  viewport: WelcomeGalaxyViewport;
}) {
  const { galaxyScale, height, stageWidth, width } = viewport;
  const scene = useMemo(() => {
    const particles = createWelcomeGalaxyParticles({
      ...FALLBACK_BUDGET,
      appearance,
      fieldStars: FALLBACK_BUDGET.fieldStars * welcomeGalaxyPalette(appearance).fieldStarDensity,
      glyphSpriteCount: GLYPH_SPRITE_COUNT,
      metrics: FALLBACK_METRICS,
      seed: FALLBACK_SEED,
      starSpriteIndex: STAR_SPRITE_INDEX,
      toneMarkSpriteIndex: TONE_MARK_SPRITE_INDEX,
    });
    const viewportSize = { galaxyScale, height, stageWidth, width };
    const transforms = new Float32Array(4 * particles.count);
    const colors = new Uint32Array(particles.count);
    writeWelcomeGalaxySprites(
      computeWelcomeGalaxyFrameState(WELCOME_GALAXY_SETTLED_T, viewportSize),
      particles,
      transforms,
      colors,
    );
    const shootOutPoints = new Float32Array(4 * Math.min(48, particles.fieldStarCount));
    const shootOutLength = writeWelcomeGalaxyShootOutStreaks(
      particles,
      viewportSize,
      WELCOME_GALAXY_SETTLED_T,
      shootOutPoints,
    );
    const streaks = Array.from({ length: shootOutLength / 4 }, (_, index) => ({
      key: index,
      x1: shootOutPoints[4 * index],
      x2: shootOutPoints[4 * index + 2],
      y1: shootOutPoints[4 * index + 1],
      y2: shootOutPoints[4 * index + 3],
    }));
    // The settled background uses rays; only the disk's glyphs and stars remain as sprites.
    const items = Array.from({ length: particles.glyphCount + particles.starCount }, (_, index) => {
      const [scaledCos = 0, scaledSin = 0, translateX = 0, translateY = 0] = transforms.subarray(
        4 * index,
        4 * index + 4,
      );
      const size = Math.hypot(scaledCos, scaledSin);
      const sprite = particles.sprite[index] ?? 0;
      return {
        centerX: translateX + (scaledCos - scaledSin) / 2,
        centerY: translateY + (scaledSin + scaledCos) / 2,
        color: toRgba(colors[index] ?? 0),
        glyph: sprite < GLYPH_SPRITE_COUNT ? WELCOME_GALAXY_GLYPHS[sprite] : undefined,
        key: index,
        size,
      };
    });
    return {
      camera: computeWelcomeGalaxyCamera(WELCOME_GALAXY_SETTLED_T, viewportSize),
      items,
      streaks,
    };
  }, [appearance, galaxyScale, height, stageWidth, width]);

  const { camera } = scene;
  const palette = welcomeGalaxyPalette(appearance);
  const degrees = (camera.positionAngle * 180) / Math.PI;
  return (
    <View
      className={`absolute inset-0 ${palette.pageClassName}`}
      pointerEvents="none"
      testID={testID}
    >
      <Svg height={height} width={width}>
        <Defs>
          <RadialGradient cx="50%" cy="50%" id="welcome-galaxy-halo" r="50%">
            {gradientStops(palette.halo)}
          </RadialGradient>
          <RadialGradient cx="50%" cy="50%" id="welcome-galaxy-core" r="50%">
            {gradientStops(palette.core)}
          </RadialGradient>
        </Defs>
        <G origin={`${camera.centerX}, ${camera.centerY}`} rotation={degrees}>
          <Ellipse
            cx={camera.centerX}
            cy={camera.centerY}
            fill="url(#welcome-galaxy-halo)"
            rx={camera.scale * 1.15}
            ry={camera.scale * 1.15 * camera.cosInclination}
          />
        </G>
        {scene.streaks.map(({ key, ...points }) => (
          <Line
            {...points}
            key={key}
            stroke={palette.streakColor}
            strokeLinecap="round"
            strokeOpacity={palette.streakAlpha * 0.65}
            strokeWidth={1.2}
          />
        ))}
        {scene.items.map((item) =>
          item.glyph === undefined ? (
            <Circle
              cx={item.centerX}
              cy={item.centerY}
              fill={item.color}
              key={item.key}
              r={Math.max(0.5, item.size / 2)}
            />
          ) : (
            <G
              fill={item.color}
              key={item.key}
              transform={`translate(${item.centerX - item.size / 2}, ${item.centerY - item.size / 2}) scale(${item.size / WELCOME_GALAXY_GLYPH_BOX})`}
            >
              {item.glyph.strokes.map((stroke, strokeIndex) => (
                // oxlint-disable-next-line react/no-array-index-key -- a glyph's strokes never reorder.
                <Path d={stroke} key={strokeIndex} />
              ))}
            </G>
          ),
        )}
        <G origin={`${camera.centerX}, ${camera.centerY}`} rotation={degrees}>
          <Ellipse
            cx={camera.centerX}
            cy={camera.centerY}
            fill="url(#welcome-galaxy-core)"
            rx={camera.scale * 0.42}
            ry={camera.scale * 0.42 * camera.cosInclination}
          />
        </G>
      </Svg>
    </View>
  );
}

/**
 * What the welcome page draws in place of the Skia scene until the scene has joined the page's
 * clock (`isSceneLive`, see `useWelcomeGalaxyClock`): the scene's canvas may still be loading, or
 * compiling its GPU pipelines ahead of time, which on a cold shader cache takes seconds. The
 * headline and buttons never wait for it; this is only what sits behind them meanwhile, on the
 * page's clock: the launch's sky (the splash's white turning to space, or the galaxy's sky closing
 * over the picture of the app after a sign-out), then the still galaxy resolving as the flight's
 * halo would (`welcomeGalaxyMaterialize`). Once the scene joins, in step or settled
 * (`welcomeGalaxySceneJoinTime`), the stand-in crossfades to it and reports `onRetired`, for the
 * page to unmount it.
 *
 * The still galaxy mounts only once the page's clock reaches the flight without the scene, so a
 * warm shader cache, whose scene joins before or during the launch, never renders it.
 */
export function WelcomeGalaxyStandIn({
  appearance,
  introTime,
  isSceneLive,
  launchKind,
  onRetired,
  viewport,
}: {
  appearance: WelcomeGalaxyAppearance;
  /** The page's clock (see `useWelcomeGalaxyClock`). */
  introTime: SharedValue<number>;
  isSceneLive: SharedValue<boolean>;
  /** What the page launches out of; a page without a launch starts on the galaxy's sky. */
  launchKind: WelcomeGalaxyLaunchKind;
  /** Called once the crossfade to the live scene has finished. */
  onRetired: () => void;
  viewport: WelcomeGalaxyViewport;
}) {
  const [isGalaxyMounted, setIsGalaxyMounted] = useState(
    () => launchKind === "splash" || introTime.get() >= 0,
  );
  useAnimatedReaction(
    () => !isSceneLive.get() && introTime.get() >= 0,
    (isNeeded, wasNeeded) => {
      if (isNeeded && wasNeeded !== true) {
        runOnJS(setIsGalaxyMounted)(true);
      }
    },
  );
  const style = useAnimatedStyle(() =>
    isSceneLive.get()
      ? {
          opacity: withTiming(0, { duration: STAND_IN_CROSSFADE_MS }, (finished) => {
            if (finished) {
              runOnJS(onRetired)();
            }
          }),
        }
      : { opacity: 1 },
  );
  const galaxyStyle = useAnimatedStyle(() => ({
    opacity: launchKind === "splash" ? 1 : welcomeGalaxyMaterialize(introTime.get()),
  }));

  return (
    <Animated.View
      className="absolute inset-0"
      pointerEvents="none"
      style={style}
      testID="auth-welcome-galaxy-stand-in"
    >
      <WelcomeGalaxyLaunchSky
        introTime={introTime}
        kind={launchKind}
        palette={welcomeGalaxyPalette(appearance)}
      />
      {isGalaxyMounted ? (
        <Animated.View className="absolute inset-0" style={galaxyStyle}>
          <WelcomeGalaxyStaticFallback
            appearance={appearance}
            testID="auth-welcome-galaxy-stand-in-galaxy"
            viewport={viewport}
          />
        </Animated.View>
      ) : null}
    </Animated.View>
  );
}
